import React, { useEffect, useState, useRef } from "react";
import DynamicForm from "../../stories/Forms/DynamicForm";
import {
  accountSchema,
  vhAccountSchema,
} from "../../stories/Forms/validationSchema";
import {
  accountFields as initialAccountFields,
  vhAccountFields,
  links,
} from "../../stories/Forms/fields";
import Toaster from "../../stories/Toasts/Toaster";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { Button } from "../../stories/Buttons/Button";
import { Row, Spinner } from "react-bootstrap";
import { editUserUrl } from "../../utils/constants";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import PageLayout from "../../components/Layout/PageLayout";
import { CheckCircleFill } from 'react-bootstrap-icons';

const Accounts = () => {
  const auth = useAuth();

  const baseFields = auth?.featureFlag ? vhAccountFields : initialAccountFields;
  const [fields, setFields] = useState(baseFields);
  const [isEdit, setIsEdit] = useState(true);
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");

  const formikRef = useRef(null);

  const {
    callApi,
    serverError,
    resultGet,
    resultPost,
    message,
    tokenError,
    isLoading,
  } = useApis();

  const handleBackClick = () => {
    globalThis.location.reload();
    setIsEdit(false);
  };

  useEffect(() => {
    auth.setTitle("Accounts");
    callApi(null, editUserUrl, "GET", {
      Authorization: `Bearer ${localStorage.getItem("access_token") || auth.accessToken}`,
      "Content-Type": "application/json",
    });
  }, [auth.accessToken]);

  // Updates form fields with fetched user data
  useEffect(() => {
    if (resultGet) {
      const updatedFields = baseFields.map((field) => {
        if (field.name && resultGet[field.name] !== undefined) {
          return {
            ...field,
            value: resultGet[field.name] || "",
            placeholder: "",
            readOnly: true,
            disabled: true,
          };
        }
        return field;
      });
      setFields(updatedFields);
      setIsEdit(true);
    }
  }, [resultGet]);

  useEffect(() => {
    if (resultPost) {
      setToasterMessage(message?.postMessage || "Operation successful");
      setShowToaster(true);
    }
  }, [resultPost, message, serverError]);

  useEffect(() => {
    if (showToaster) {
      const timer = setTimeout(() => {
        setShowToaster(false);
        setToasterMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showToaster]);

  // Prepares fields for editing, excluding certain fields
  const handleEdit = () => {
    const updatedFields = fields.map((field) => {
      const originalField = baseFields.find((f) => f.name === field.name);

      if (field.name === "phlebotomist_org" || field.name === "email") {
        return {
          ...field,
          readOnly: true,
          disabled: true,
          placeholder: "",
        };
      }
      return {
        ...field,
        readOnly: false,
        disabled: false,
        placeholder: originalField ? originalField.placeholder : "",
      };
    });
    setFields(updatedFields);
    setIsEdit(false);
  };

  const handleFieldChange = (name, value) => {
    const updatedFields = fields.map((field) =>
      field.name === name ? { ...field, value } : field,
    );
    setFields(updatedFields);
  };

  let userName;
  const submitAccountUpdate = async () => {
    let firstName = "";
    let lastName = "";

    const payload = fields.reduce((acc, field) => {
      acc[field.name] = field.value;

      if (field.name === 'first_name') {
        firstName = field.value;
      } else if (field.name === 'last_name') {
        lastName = field.value;
      }

      return acc;
    }, {});

    userName = `${firstName} ${lastName}`.trim();
    localStorage.setItem("username", userName);

    try {
      await callApi(payload, editUserUrl, "PUT", {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        "Content-Type": "application/json",
      });

      callApi(null, editUserUrl, "GET", {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        "Content-Type": "application/json",
      });

      const updatedFields = fields.map((field) => ({
        ...field,
        readOnly: true,
        disabled: true,
      }));

      setFields(updatedFields);
      setToasterMessage(
        message?.postMessage || "User details updated successfully.",
      );
      setShowToaster(true);
      setIsEdit(true);
    } catch (error) {
      setToasterMessage(
        error.message || "An error occurred while saving data.",
      );
      setShowToaster(true);
    }
  };

  const handleSubmit = async () => {
    if (!formikRef.current) {
      return;
    }

    const errors = await formikRef.current.validateForm();

    const allTouched = fields.reduce((acc, f) => {
      if (f.type !== "button" && f.type !== "submit") acc[f.name] = true;
      return acc;
    }, {});
    formikRef.current.setTouched(allTouched);

    if (errors && Object.keys(errors).length > 0) {
      return;
    }

    await submitAccountUpdate();
  };

  const onToasterClose = () => {
    setShowToaster(false);
    setToasterMessage("");
  };

  const getHeaderText = () => {
    if (tokenError) return "Error";
    if (serverError) return "Error";
    return "Success";
  };

  const headerText = getHeaderText();

  useEffect(() => {
    if (auth?.featureFlag && (showToaster || tokenError)) {
      auth?.showToast(headerText, tokenError || toasterMessage,undefined,{icon: <CheckCircleFill />,className: auth?.featureFlag && "vh-toaster",});
    }
  }, [showToaster, tokenError, auth?.featureFlag]);

  const accountContent = (
    <>
      {!auth?.featureFlag && (
        <p className="account-form-title">
          To update your information, please edit the fields below:
        </p>
      )}

      <div className="edit-account-card card">
        <Row className="account-section">
          <DynamicForm
            askConfirmation={true}
            data={resultGet}
            fields={fields}
            formikRef={formikRef}
            onChange={handleFieldChange}
            onSubmit={submitAccountUpdate}
            serverError={serverError}
            validationSchema={
              auth?.featureFlag ? vhAccountSchema : accountSchema
            }
            validateOnChange={false}
          />
        </Row>

        {!auth?.featureFlag && (showToaster || tokenError) && (
          <Toaster
            body={
              tokenError
                ? "Your session has expired. Please log in again."
                : toasterMessage
            }
            header={headerText}
            onClose={onToasterClose}
            position="top-end"
            show={tokenError || showToaster}
          />
        )}

        {isEdit ? (
          <Button
            label="Edit"
            className="account-edit-btn"
            onClick={handleEdit}
            size="small"
            variant="primary"
          />
        ) : (
          <div className="d-flex flex-row-reverse">
            <Button
              className={`m-2 ml-0 ${auth?.featureFlag && "me-0"}`}
              label="Save changes"
              onClick={handleSubmit}
              size={!auth?.featureFlag && "small"}
              type="button"
              variant="primary"
            />
            <Button
              className="btn-cancel m-2 ml-0"
              label="Cancel"
              onClick={handleBackClick}
              size="small"
            />
          </div>
        )}
      </div>
    </>
  );

  if (auth.featureFlagLoading || isLoading)
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );

  return (
    <LoggedInLayout
      buttonLabel="Logout"
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      vitalityLayout={auth?.featureFlag}
    >
      {auth?.featureFlag ? (
        <PageLayout
          heading="My account"
          subHeading="View and edit your professional account details"
        >
          {accountContent}
        </PageLayout>
      ) : (
        accountContent
      )}
    </LoggedInLayout>
  );
};

export default Accounts;
